import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

import { useUserByWhereQuery } from '@/__generated__/public-graphql';
import { CamperTop } from '@/contents/CampSite/camper';
import { MypageTop } from '@/contents/CampSite/mypage';
import { GlobalContext } from '@/contents/hooks/state';
import LayoutCamper from '@/layouts/LayoutCamper';
import getPublicUserById from '@/modules/User/public/getPublicUserById';
import useGetLoginUser from '@/modules/User/user/useGetLoginUser';
import type { NextPageWithLayoutPublicUser } from '@/types';
import type { PublicUser } from '@/types/domain';
import { Page } from '@/types/domain';
import ImageUrlConvert from '@/utils/ImageUrlConvert';

type Props = {
  user: PublicUser | null;
};

const Index: NextPageWithLayoutPublicUser = ({ user: propUser }: Props) => {
  const router = useRouter();
  const { setPage } = useContext(GlobalContext);
  const { user: loginUser, isLogin } = useGetLoginUser();
  const { data } = useUserByWhereQuery(
    {
      where: {
        id: {
          _eq: propUser?.id,
        },
      },
    },
    {
      enabled: !!propUser?.id,
    }
  );
  const user = data?.user_public[0] ?? propUser;

  useEffect(() => {
    if (isLogin && user?.id === loginUser?.id) {
      setPage(Page.MYPAGE_ACCOUNT);
    } else {
      setPage(Page.CAMPER_ACCOUNT);
    }
  }, [router]);

  if (!user) return <></>;

  return (
    <>
      <Head>
        <title>{`${user.display_name}さんのマイページ - iihi`}</title>
        <meta
          name="description"
          content={`iihiを利用している${user.display_name}さんのマイページです。${user.display_name}さんのお気に入りやレビューから新たなキャンプ場や楽しみ方を見つけよう！`}
        />
        <meta
          property="og:url"
          content={`https://iihi.life/camper/${user.id}`}
        />
        <meta
          property="og:title"
          content={`${user.display_name}さんのマイページ - iihi`}
        />
        <meta
          property="og:description"
          content={`iihiを利用している${user.display_name}さんのマイページです。${user.display_name}さんのお気に入りやレビューから新たなキャンプ場や楽しみ方を見つけよう！`}
        />
        <meta
          property="og:image"
          content={
            user.icon?.file_name
              ? ImageUrlConvert(user.icon?.file_name, true)
              : 'https://iihi.life/assets/images/common/ogp.jpg'
          }
        />
      </Head>
      <>
        {loginUser &&
        typeof loginUser === 'object' &&
        user.id === loginUser.id ? (
          <MypageTop publicUser={user} />
        ) : (
          <CamperTop user={user} />
        )}
      </>
    </>
  );
};

Index.getLayout = (page) => {
  const { user } = page.props;
  if (!user) {
    <></>;
  }
  return <LayoutCamper>{page}</LayoutCamper>;
};

export default Index;

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: true,
  };
};

export async function getStaticProps({ params }: any) {
  const user = await getPublicUserById(params.id);
  if (!user) {
    return {
      notFound: true,
    };
  }

  return { props: { user }, revalidate: 60 };
}
